/**
 * Request Handler
 * Handles HTTP requests to Gateway API (direct or proxied through TYPO3)
 *
 * ⚠️ SECURITY: ALL requests MUST include proper authorization.
 * See /SECURITY_GUIDELINES.md for mandatory security requirements.
 */

import type { ApiResponse, AuthService } from './types';
import type { LabelGroups } from '../relations/default-interaction-labels';
import { validateGatewayUrl } from '../app-constants';
import {
  getRequiredCsrfToken,
  assertAuthorized,
  buildProxyHeaders,
  buildDirectHeaders,
} from '../auth-utils';
import { GatewayError, GatewayErrorCode, isGatewayError } from './error-codes';
import {
  hasSessionEnded,
  pendingIfSessionEnded,
  suspendIfSessionEnded,
} from '../session-ended';
import { logger } from '../logger';

export interface RequestHandlerOptions {
  useProxy: boolean;
  proxyUrl: string | null;
  baseUrl: string;
  endpoint: string;
  authService: AuthService | null;
}

export class RequestHandler {
  private options: RequestHandlerOptions;
  private authToken: string | null = null;
  private onRateLimitCallback: (() => void) | null = null;

  constructor(options: RequestHandlerOptions) {
    this.options = options;
  }

  /**
   * Set authentication token for direct Gateway access
   * @param token - Authentication token
   */
  setAuthToken(token: string | null): void {
    this.authToken = token;
  }

  /**
   * Get current authentication token
   */
  getAuthToken(): string | null {
    return this.authToken;
  }

  /**
   * Check if handler is ready to make requests
   */
  isReady(): boolean {
    if (this.options.useProxy) {
      return true; // Proxy mode doesn't need auth token
    }
    return this.authToken !== null;
  }

  /**
   * Set callback for rate limit events
   */
  setRateLimitCallback(callback: () => void): void {
    this.onRateLimitCallback = callback;
  }

  /**
   * Fetch the gateway `/config/labels` localisation seed data.
   *
   * This is a plain GET (not a `/do` command):
   *  - proxy mode  -> GET {proxyUrl with type=3005}, credentials included
   *  - direct mode -> GET {baseUrl}/config/labels with the Bearer header
   *
   * @throws GatewayError on a non-ok response or when direct mode is not ready
   */
  async getLabels(): Promise<LabelGroups> {
    if (this.options.useProxy && this.options.proxyUrl) {
      const labelsUrl = new URL(this.options.proxyUrl);
      labelsUrl.searchParams.set('type', '3005');
      const url = labelsUrl.toString();

      // SECURITY: Validate URL against allowlist to prevent SSRF
      validateGatewayUrl(url);

      // Read-only GET: the gitterdan_session cookie authorises it; no X-CSRF-Token
      // is needed (CSRF protection applies to state-changing requests only).
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      await assertAuthorized(response);
      if (!response.ok) {
        throw new GatewayError(
          `Gateway labels request failed: ${response.status} ${response.statusText}`,
          GatewayErrorCode.NETWORK_ERROR,
          { status: response.status, statusText: response.statusText }
        );
      }
      return (await response.json()) as LabelGroups;
    }

    if (!this.isReady()) {
      throw new GatewayError(
        'Gateway service not initialized. Call initialize() first.',
        GatewayErrorCode.NOT_INITIALIZED
      );
    }

    const url = `${this.options.baseUrl}/config/labels`;

    // SECURITY: Validate URL against allowlist to prevent SSRF
    validateGatewayUrl(url);

    const response = await fetch(url, {
      method: 'GET',
      headers: buildDirectHeaders(this.authToken!, false),
    });
    if (!response.ok) {
      throw new GatewayError(
        `Gateway labels request failed: ${response.status} ${response.statusText}`,
        GatewayErrorCode.NETWORK_ERROR,
        { status: response.status, statusText: response.statusText }
      );
    }
    return (await response.json()) as LabelGroups;
  }

  /**
   * Make a request to the Gateway API (auto-selects proxy or direct mode)
   * @param command - The command to execute
   * @param language - ISO 639-3 language code
   * @param data - Additional data for the command
   * @param signal - Optional AbortSignal for request cancellation
   * @returns API response
   * @throws If request fails or service not ready
   */
  makeRequest(
    command: string,
    language: string = 'eng',
    data: string = '{}',
    signal?: AbortSignal
  ): Promise<ApiResponse> {
    if (this.options.useProxy && this.options.proxyUrl) {
      return this.makeProxyRequest(command, language, data, signal);
    }

    return this.makeDirectRequest(command, language, data, signal);
  }

  /**
   * Make a direct request to Gateway API
   * @param command - The command to execute
   * @param language - ISO 639-3 language code
   * @param data - Additional data for the command
   * @param signal - Optional AbortSignal for request cancellation
   * @returns API response
   * @throws If request fails
   */
  private async makeDirectRequest(
    command: string,
    language: string,
    data: string,
    signal?: AbortSignal
  ): Promise<ApiResponse> {
    if (!this.isReady()) {
      throw new GatewayError(
        'Gateway service not initialized. Call initialize() first.',
        GatewayErrorCode.NOT_INITIALIZED
      );
    }

    const url = `${this.options.baseUrl}${this.options.endpoint}`;
    const payload = {
      command: command,
      language: language,
      data: data,
    };

    try {
      logger.debug(
        `Making Gateway API request: ${command} with language: ${language}`
      );
      logger.debug('Payload:', JSON.stringify(payload));

      // SECURITY: Validate URL against allowlist to prevent SSRF
      validateGatewayUrl(url);

      const response = await fetch(url, {
        method: 'POST',
        headers: buildDirectHeaders(this.authToken!),
        body: JSON.stringify(payload),
        signal,
      });

      if (!response.ok) {
        // Log full error details server-side only
        try {
          const errorBody = await response.text();
          logger.error('Gateway API error response body:', errorBody);
        } catch (e) {
          logger.error('Could not read error response body:', e);
        }

        if (response.status === 422) {
          throw new GatewayError(
            'Document text exceeds the Gateway character limit.',
            GatewayErrorCode.PAYLOAD_TOO_LARGE,
            { status: 422 }
          );
        }

        if (response.status === 409) {
          throw new GatewayError(
            'Gateway session expired.',
            GatewayErrorCode.SESSION_EXPIRED,
            { status: 409 }
          );
        }

        throw new GatewayError(
          `Gateway API request failed: ${response.status} ${response.statusText}`,
          GatewayErrorCode.NETWORK_ERROR,
          { status: response.status, statusText: response.statusText }
        );
      }

      const result: ApiResponse = await response.json();
      logger.debug(`Gateway API response received for ${command}:`, result);

      return result;
    } catch (error) {
      // Re-throw GatewayError as-is (includes our own throws above)
      if (error instanceof GatewayError) {
        logger.error(`Gateway API error for command ${command}:`, error);
        logger.error('Request payload was:', payload);
        throw error;
      }

      // Preserve original error for better stack traces
      const originalError = error as Error;
      logger.error(`Gateway API error for command ${command}:`, originalError);
      logger.error('Request payload was:', payload);

      // Wrap in GatewayError with expected message format
      const gatewayError = new GatewayError(
        `Gateway API request failed: ${originalError.message}`,
        GatewayErrorCode.NETWORK_ERROR
      );
      // Preserve the original stack trace for debugging
      if (originalError.stack) {
        gatewayError.stack = originalError.stack;
      }
      throw gatewayError;
    }
  }

  /**
   * Make a proxied request through TYPO3
   * @param command - The command to execute
   * @param language - ISO 639-3 language code
   * @param data - Additional data for the command
   * @param signal - Optional AbortSignal for request cancellation
   * @returns API response
   * @throws If request fails or user not authenticated
   */
  private async makeProxyRequest(
    command: string,
    language: string,
    data: string,
    signal?: AbortSignal,
    isRetry: boolean = false
  ): Promise<ApiResponse> {
    const payload = {
      command: command,
      language: language,
      data: data,
    };

    // After a session loss the dialog owns the screen: fire nothing, throw nothing.
    const pending = pendingIfSessionEnded<ApiResponse>();
    if (pending) return pending;

    // Get CSRF token from auth service
    const csrfToken = getRequiredCsrfToken(this.options.authService);

    try {
      // SECURITY: Validate URL against allowlist to prevent SSRF
      validateGatewayUrl(this.options.proxyUrl!);

      const response = await fetch(this.options.proxyUrl!, {
        method: 'POST',
        credentials: 'include', // Send session cookie (gitterdan_session)
        headers: buildProxyHeaders(csrfToken),
        body: JSON.stringify(payload),
        signal,
      });

      // Handle common auth errors
      await assertAuthorized(response);

      if (!response.ok) {
        let isRateLimitError = false;
        try {
          const errorBody = await response.text();

          if (response.status === 422) {
            throw new GatewayError(
              'Document text exceeds the Gateway character limit.',
              GatewayErrorCode.PAYLOAD_TOO_LARGE,
              { status: 422 }
            );
          }

          if (response.status === 409) {
            throw new GatewayError(
              'Gateway session expired.',
              GatewayErrorCode.SESSION_EXPIRED,
              { status: 409 }
            );
          }

          // Detect rate limiting
          if (
            errorBody.includes('Rate limit exceeded') ||
            errorBody.includes('rate limit')
          ) {
            isRateLimitError = true;
            this.handleRateLimit();
          } else {
            logger.error('Gateway proxy error response body:', errorBody);
          }
        } catch (e) {
          if (e instanceof GatewayError) throw e;
          logger.error('Could not read error response body:', e);
        }

        if (isRateLimitError) {
          throw new GatewayError(
            'Rate limit exceeded',
            GatewayErrorCode.RATE_LIMITED
          );
        }

        throw new GatewayError(
          `Gateway proxy request failed: ${response.status} ${response.statusText}`,
          GatewayErrorCode.NETWORK_ERROR,
          { status: response.status, statusText: response.statusText }
        );
      }

      const result: ApiResponse = await response.json();
      return result;
    } catch (error) {
      // imsight#106: a 401 is final. The notifier shows the signed-out dialog and
      // the caller stays pending, so no error toast appears behind it.
      const suspended = suspendIfSessionEnded<ApiResponse>(error);
      if (suspended) return suspended;

      // A CSRF rejection usually means TYPO3 rotated the token: refresh once, retry.
      if (
        !isRetry &&
        isGatewayError(error, GatewayErrorCode.CSRF_REJECTED) &&
        typeof this.options.authService?.refreshSession === 'function'
      ) {
        logger.info(
          'Proxy rejected the CSRF token — refreshing session and retrying once'
        );
        const refreshed = await this.options.authService.refreshSession();
        if (refreshed) {
          return this.makeProxyRequest(command, language, data, signal, true);
        }
        if (hasSessionEnded()) return new Promise<ApiResponse>(() => {});
        logger.warn('Session refresh failed; bubbling CSRF error');
      }

      // Re-throw GatewayError as-is
      if (error instanceof GatewayError) {
        if (error.code !== GatewayErrorCode.RATE_LIMITED) {
          logger.error(`Gateway proxy error for command ${command}:`, error);
          logger.error('Request payload was:', payload);
        }
        throw new GatewayError(
          `Gateway proxy request failed: ${error.message}`,
          error.code,
          error.details
        );
      }

      logger.error(`Gateway proxy error for command ${command}:`, error);
      logger.error('Request payload was:', payload);
      throw new GatewayError(
        `Gateway proxy request failed: ${(error as Error).message}`,
        GatewayErrorCode.NETWORK_ERROR
      );
    }
  }

  /**
   * Handle rate limit detection
   */
  private handleRateLimit(): void {
    logger.warn('Rate limit detected - notifying service');

    if (this.onRateLimitCallback) {
      this.onRateLimitCallback();
    }
  }
}
